// frontend/src/components/part/PartCard.js
import React from 'react';
import { FaShoppingCart, FaRocket } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import helpers from '../../utils/helpers';
import { useCart } from '../../context/CartContext';
import SaveButton from '../common/SaveButton';

function PartCard({ part }) {
  const { addToCart } = useCart();

  const handleAddToCart = (e) => {
    e.preventDefault();
    
    if (!part || !part.id || !part.price || part.price <= 0) {
        alert("Cannot add this item to cart: Price or ID is missing/invalid.");
        return;
    }

    addToCart(part);
    alert(`${part.itemname} added to cart!`);
  };

  const imageSrc = part.itemurl || 
                   (part.images && part.images.front_jpg) || 
                   (part.images && Object.values(part.images)[0]) || 
                   'https://via.placeholder.com/300x300?text=No+Image';

  const isNewArrival = () => {
      if (!part.created_at) return false;
      const createdDate = new Date(part.created_at);
      const today = new Date();
      const differenceInTime = today.getTime() - createdDate.getTime();
      const differenceInDays = differenceInTime / (1000 * 3600 * 24);
      return differenceInDays <= 30; 
  };

  const getConditionLabel = () => {
      const cond = part.condition ? part.condition.toLowerCase() : '';
      if (cond.includes('new')) return 'Brand New';
      if (cond.includes('used')) return 'Used';
      if (cond.includes('remanufactured')) return 'Refurbished';
      return part.condition; 
  };

  const isPromoted = part.is_promoted && new Date(part.promoted_until) > new Date();

  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col h-full group relative w-full ${isPromoted ? 'border-2 border-red-500 ring-2 ring-red-100' : ''}`}>
      
      {/* Save Button */}
      <div className="absolute top-2 right-2 z-10">
        <SaveButton itemId={part.id} itemType="part" />
      </div>

      <Link to={`/parts/${part.id}`} className="relative h-48 bg-gray-200 overflow-hidden block">
        <img 
          src={imageSrc} 
          alt={part.itemname} 
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        
        {/* BADGE 1: NEW ARRIVAL */}
        {isNewArrival() && (
          <span className="absolute top-2 left-2 bg-green-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wide">
            New Arrival
          </span>
        )}

        {/* BADGE 2: BOOSTED */}
        {isPromoted && (
            <span className="absolute top-8 left-2 bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wide flex items-center gap-1 animate-pulse">
                <FaRocket size={8} /> FEATURED
            </span>
        )}

        {/* BADGE 3: CONDITION */}
        <span className={`absolute bottom-2 left-2 text-[10px] font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wide ${
            getConditionLabel() === 'Brand New' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-white'
        }`}>
            {getConditionLabel()}
        </span>

      </Link>

      <div className="p-4 flex-grow flex flex-col">
        <div className="text-xs text-gray-500 mb-1 uppercase tracking-wide break-words">
          {part.make} {part.model}
        </div>
        
        <Link to={`/parts/${part.id}`} className="block mb-2">
          <h3 className="text-lg font-semibold text-gray-800 line-clamp-2 leading-tight hover:text-primary transition-colors break-words">
            {part.itemname}
          </h3>
        </Link>
        
        <p className="text-sm text-gray-600 mb-4 line-clamp-2 flex-grow break-words">
          {part.description}
        </p>

        <div className="mt-auto flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500">Price</span>
            <span className="text-xl font-bold text-primary">
              {helpers.formatCurrency(part.price, 'NGN')}
            </span>
          </div>
          
          <button 
            className="bg-gray-100 hover:bg-primary hover:text-white text-gray-800 p-2 rounded-full transition-colors duration-300 flex-shrink-0"
            aria-label="Add to Cart"
            onClick={handleAddToCart}
          >
            <FaShoppingCart />
          </button>
        </div>
      </div>
    </div>
  );
}

export default PartCard;